import { supabase } from '@/features/sistema/services/supabaseClient'

interface CreateRelatorioData {
  obra_id: string;
  data: string;
  copy_clima: boolean;
  copy_mao_obra: boolean;
  copy_equipamentos: boolean;
  copy_atividades: boolean;
  copy_ocorrencias: boolean;
  copy_comentarios: boolean;
}

export interface Relatorio {
  id: string;
  obra_id: string;
  data: string;
  status: 'rascunho' | 'submetido' | 'aprovado';
  numero: number;
  criado_por?: string | null;
  updated_at?: string;
  created_at?: string;
  comentarios?: string | null;
  clima_manha?: string | null;
  clima_tarde?: string | null;
  clima_noite?: string | null;
  ultima_modificacao?: string | null;
  alteracoes_projeto?: any | null;
}

interface AnexoUploadResult {
  id: string;
  url: string;
  nome: string;
  tipo: "foto" | "video" | "anexo";
  criado_em: string;
}

interface CompressionOptions {
  maxSizeKB: number;
  maxWidth: number;
  maxHeight: number;
  quality: number;
}

// ✅ FUNÇÃO HELPER: Verificar se está online
function isOnline(): boolean {
  return navigator.onLine;
}

// ✅ NOVA FUNÇÃO: Compressão de imagens
export async function compressImage(
  file: File, 
  options: Partial<CompressionOptions> = {}
): Promise<File> {
  const defaultOptions: CompressionOptions = {
    maxSizeKB: 200,
    maxWidth: 1920,
    maxHeight: 1080,
    quality: 0.8
  };
  
  const opts = { ...defaultOptions, ...options };
  
  return new Promise((resolve) => {
    // Se não é uma imagem ou já está pequeno o suficiente, retorna original
    if (!file.type.startsWith('image/') || file.size <= opts.maxSizeKB * 1024) {
      resolve(file);
      return;
    }

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();

    img.onload = () => {
      try {
        // Calcular novas dimensões mantendo proporção
        let { width, height } = img;
        
        if (width > opts.maxWidth || height > opts.maxHeight) {
          const ratioWidth = opts.maxWidth / width;
          const ratioHeight = opts.maxHeight / height;
          const ratio = Math.min(ratioWidth, ratioHeight);
          
          width = Math.round(width * ratio);
          height = Math.round(height * ratio);
        }

        canvas.width = width;
        canvas.height = height;

        // Desenhar imagem redimensionada
        ctx!.fillStyle = '#FFFFFF';
        ctx!.fillRect(0, 0, width, height);
        ctx!.drawImage(img, 0, 0, width, height);

        // Função para tentar diferentes qualidades até atingir o tamanho desejado
        const tryCompress = (quality: number): void => {
          canvas.toBlob((blob) => {
            if (!blob) {
              console.error('Erro na compressão da imagem');
              resolve(file);
              return;
            }

            // Se ainda está muito grande e qualidade pode ser reduzida
            if (blob.size > opts.maxSizeKB * 1024 && quality > 0.1) {
              tryCompress(quality - 0.1);
              return;
            }

            // Criar novo File com o blob comprimido
            const compressedFile = new File([blob], file.name, {
              type: file.type,
              lastModified: Date.now(),
            });

            resolve(compressedFile);
          }, file.type, quality);
        };

        tryCompress(opts.quality);
      } catch (error) {
        console.error('Erro na compressão:', error);
        resolve(file); // Retorna original em caso de erro
      }
    };

    img.onerror = () => {
      console.error('Erro ao carregar imagem para compressão');
      resolve(file); // Retorna original em caso de erro
    };

    // Carregar imagem
    const reader = new FileReader();
    reader.onload = (e) => {
      img.src = e.target?.result as string;
    };
    reader.onerror = () => {
      console.error('Erro ao ler arquivo para compressão');
      resolve(file);
    };
    reader.readAsDataURL(file);
  });
}

// ✅ NOVA FUNÇÃO: Comprime múltiplas imagens
export async function compressImages(
  files: FileList | File[],
  options: Partial<CompressionOptions> = {}
): Promise<File[]> {
  const fileArray = Array.from(files);
  const promises = fileArray.map(file => compressImage(file, options));
  return Promise.all(promises);
}

// ✅ NOVA FUNÇÃO: Verificar se um arquivo é uma imagem válida
export function isValidImage(file: File): boolean {
  const validTypes = [
    'image/jpeg',
    'image/jpg', 
    'image/png',
    'image/webp',
    'image/gif'
  ];
  return validTypes.includes(file.type.toLowerCase());
}

// ✅ NOVA FUNÇÃO: Upload de anexos com compressão automática
export async function uploadAnexo(
  file: File,
  relatorioId: string,
  tipo: "foto" | "video" | "anexo",
  compressionOptions?: Partial<CompressionOptions>
): Promise<AnexoUploadResult> {
  if (!isOnline()) {
    throw new Error('Upload de arquivos não disponível offline. Conecte-se à internet.');
  }

  try {
    // Comprimir imagem se necessário
    let processedFile = file;
    if (tipo === "foto" && isValidImage(file)) {
      console.log(`📷 Comprimindo imagem: ${file.name} (${(file.size / 1024).toFixed(1)}KB)`);
      processedFile = await compressImage(file, compressionOptions);
    }

    const fileExt = processedFile.name.split(".").pop();
    const fileName = `${relatorioId}/${tipo}/${Date.now()}.${fileExt}`;
    
    // Upload para o storage
    const { error: uploadError } = await supabase.storage
      .from("anexos")
      .upload(fileName, processedFile, {
        cacheControl: "3600",
        upsert: false,
      });

    if (uploadError) {
      throw new Error(`Erro no upload: ${uploadError.message}`);
    }

    // Obter URL pública
    const { data: urlData } = supabase.storage
      .from("anexos")
      .getPublicUrl(fileName);

    if (!urlData.publicUrl) {
      throw new Error("Erro ao obter URL pública");
    }

    // Salvar no banco de dados
    const tableName = tipo === "foto" ? "fotos" : tipo === "video" ? "videos" : "anexos";
    const insertData = {
      relatorio_id: relatorioId,
      url: urlData.publicUrl,
      nome: processedFile.name,
      criado_em: new Date().toISOString(),
    };

    const { data: dbData, error: dbError } = await supabase
      .from(tableName)
      .insert(insertData)
      .select('*')
      .single();

    if (dbError) {
      // Se erro no banco, tentar remover do storage
      try {
        await supabase.storage.from("anexos").remove([fileName]);
      } catch (cleanupError) {
        console.warn('Erro ao limpar arquivo do storage:', cleanupError);
      }
      throw new Error(`Erro ao salvar no banco: ${dbError.message}`);
    }

    return {
      id: dbData.id,
      url: urlData.publicUrl,
      nome: processedFile.name,
      tipo,
      criado_em: new Date().toISOString(),
    };

  } catch (error) {
    console.error('Erro no uploadAnexo:', error);
    throw error;
  }
}

// ✅ NOVA FUNÇÃO: Upload múltiplo de anexos
export async function uploadMultipleAnexos(
  files: FileList | File[],
  relatorioId: string,
  tipo: "foto" | "video" | "anexo",
  compressionOptions?: Partial<CompressionOptions>
): Promise<AnexoUploadResult[]> {
  const fileArray = Array.from(files);
  const results: AnexoUploadResult[] = [];
  const errors: string[] = [];

  for (const file of fileArray) {
    try {
      const result = await uploadAnexo(file, relatorioId, tipo, compressionOptions);
      results.push(result);
    } catch (error) {
      console.error(`Erro ao fazer upload de ${file.name}:`, error);
      errors.push(`${file.name}: ${error}`);
    }
  }

  if (errors.length > 0 && results.length === 0) {
    throw new Error(`Falha em todos os uploads: ${errors.join(', ')}`);
  }

  if (errors.length > 0) {
    console.warn(`Alguns uploads falharam: ${errors.join(', ')}`);
  }

  return results;
}

// ✅ NOVA FUNÇÃO: Remover anexo
export async function removeAnexo(anexoUrl: string, tipo: "foto" | "video" | "anexo"): Promise<void> {
  if (!isOnline()) {
    throw new Error('Remoção de anexos não disponível offline. Conecte-se à internet.');
  }

  try {
    // Remover do storage
    const filePath = anexoUrl.split("/anexos/")[1];
    if (filePath) {
      const { error: storageError } = await supabase.storage
        .from("anexos")
        .remove([filePath]);

      if (storageError) {
        console.warn('Erro ao remover do storage:', storageError);
      }
    }

    // Remover do banco
    const tableName = tipo === "foto" ? "fotos" : tipo === "video" ? "videos" : "anexos";
    const { error: dbError } = await supabase
      .from(tableName)
      .delete()
      .eq("url", anexoUrl);

    if (dbError) {
      throw new Error(`Erro ao remover do banco: ${dbError.message}`);
    }


  } catch (error) {
    console.error('Erro ao remover anexo:', error);
    throw error;
  }
}

// ✅ NOVA FUNÇÃO: Buscar relatórios COM CACHE-FIRST
export async function getRelatoriosByObra(obraId: string): Promise<Relatorio[]> {
  try {
    console.log(`📊 getRelatoriosByObra: ${isOnline() ? 'Online' : 'Offline'} - obra ${obraId}`);

    if (isOnline()) {
      // ✅ Online: Buscar do servidor
      const { data, error } = await supabase
        .from('relatorios')
        .select('*')
        .eq('obra_id', obraId)
        .order('numero', { ascending: false });

      if (error) throw error;

      // ✅ CACHEAR automaticamente (usar store genérica)
      try {
        localStorage.setItem(`relatorios_${obraId}`, JSON.stringify(data || []));
        localStorage.setItem(`relatorios_${obraId}_timestamp`, Date.now().toString());
      } catch (cacheError) {
        console.warn('Erro ao cachear relatórios:', cacheError);
      }

      return data || [];

    } else {
      // ✅ Offline: Buscar do cache
      console.log('📱 getRelatoriosByObra: Modo offline, buscando do cache...');
      
      try {
        const cached = localStorage.getItem(`relatorios_${obraId}`);
        if (cached) {
          const relatorios = JSON.parse(cached);
          console.log(`📱 getRelatoriosByObra: ${relatorios.length} relatórios carregados do cache`);
          return relatorios;
        } else {
          console.log('📱 getRelatoriosByObra: Nenhum relatório em cache');
          return [];
        }
      } catch (cacheError) {
        console.error('❌ Erro ao carregar relatórios do cache:', cacheError);
        return [];
      }
    }

  } catch (error) {
    console.error('❌ Erro em getRelatoriosByObra:', error);
    
    if (!isOnline()) {
      // ✅ Fallback: tentar cache se houver erro offline
      try {
        const cached = localStorage.getItem(`relatorios_${obraId}`);
        if (cached) {
          const relatorios = JSON.parse(cached);
          return relatorios;
        }
      } catch (fallbackError) {
        console.error('Erro no fallback cache:', fallbackError);
      }
    }
    
    throw error;
  }
}

// ✅ NOVA FUNÇÃO: Buscar relatório específico COM CACHE
export async function getRelatorioById(relatorioId: string): Promise<Relatorio | null> {
  try {
    console.log(`📊 getRelatorioById: ${isOnline() ? 'Online' : 'Offline'} - relatório ${relatorioId}`);

    if (isOnline()) {
      // ✅ Online: Buscar do servidor
      const { data, error } = await supabase
        .from('relatorios')
        .select('*')
        .eq('id', relatorioId)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          return null; // Não encontrado
        }
        throw error;
      }

      // ✅ CACHEAR automaticamente
      try {
        localStorage.setItem(`relatorio_${relatorioId}`, JSON.stringify(data));
        localStorage.setItem(`relatorio_${relatorioId}_timestamp`, Date.now().toString());
      } catch (cacheError) {
        console.warn('Erro ao cachear relatório:', cacheError);
      }

      return data;

    } else {
      // ✅ Offline: Buscar do cache
      console.log('📱 getRelatorioById: Modo offline, buscando do cache...');
      
      try {
        const cached = localStorage.getItem(`relatorio_${relatorioId}`);
        if (cached) {
          const relatorio = JSON.parse(cached);
          console.log(`📱 getRelatorioById: Relatório ${relatorioId} carregado do cache`);
          return relatorio;
        } else {
          console.log('📱 getRelatorioById: Relatório não encontrado em cache');
          return null;
        }
      } catch (cacheError) {
        console.error('❌ Erro ao carregar relatório do cache:', cacheError);
        return null;
      }
    }

  } catch (error) {
    console.error('❌ Erro em getRelatorioById:', error);
    throw error;
  }
}

// ✅ FUNÇÃO ORIGINAL MANTIDA: Criar relatório
export const createRelatorio = async (data: CreateRelatorioData): Promise<string> => {
  console.log(`➕ createRelatorio: ${isOnline() ? 'Online' : 'Offline'}`);

  if (!isOnline()) {
    throw new Error('Criação de relatórios não disponível offline. Conecte-se à internet.');
  }

  // Buscar o próximo número de relatório
  const { data: lastRelatorio } = await supabase
    .from('relatorios')
    .select('numero')
    .eq('obra_id', data.obra_id)
    .order('numero', { ascending: false })
    .limit(1);

  const nextNumero = lastRelatorio && lastRelatorio.length > 0 
    ? lastRelatorio[0].numero + 1 
    : 1;

  // Criar o novo relatório
  const { data: novoRelatorio, error } = await supabase
    .from('relatorios')
    .insert({
      obra_id: data.obra_id,
      data: data.data,
      numero: nextNumero,
      status: 'rascunho',
    })
    .select('id')
    .single();

  if (error) {
    throw new Error(`Erro ao criar relatório: ${error.message}`);
  }

  // Copiar dados do último relatório se solicitado
  if (data.copy_clima || data.copy_mao_obra || data.copy_equipamentos || 
      data.copy_atividades || data.copy_ocorrencias || data.copy_comentarios) {
    
    const { data: ultimoRelatorio } = await supabase
      .from('relatorios')
      .select('*')
      .eq('obra_id', data.obra_id)
      .order('numero', { ascending: false })
      .limit(1);

    if (ultimoRelatorio && ultimoRelatorio.length > 0) {
      const ultimo = ultimoRelatorio[0];
      
      // Copiar dados conforme solicitado
      const updateData: any = {};
      
      if (data.copy_clima && ultimo.clima) {
        updateData.clima = ultimo.clima;
      }
      if (data.copy_comentarios && ultimo.comentarios) {
        updateData.comentarios = ultimo.comentarios;
      }
      if (data.copy_ocorrencias && ultimo.ocorrencias) {
        updateData.ocorrencias = ultimo.ocorrencias;
      }

      if (Object.keys(updateData).length > 0) {
        await supabase
          .from('relatorios')
          .update(updateData)
          .eq('id', novoRelatorio.id);
      }
    }
  }

  // ✅ INVALIDAR cache da obra após criar relatório
  try {
    localStorage.removeItem(`relatorios_${data.obra_id}`);
    localStorage.removeItem(`relatorios_${data.obra_id}_timestamp`);
  } catch (error) {
    console.warn('Erro ao invalidar cache de relatórios:', error);
  }

  return novoRelatorio.id;
};

// ✅ NOVA FUNÇÃO: Atualizar relatório COM CACHE
export async function updateRelatorio(relatorioId: string, updates: Partial<Relatorio>): Promise<Relatorio> {
  try {

    if (!isOnline()) {
      throw new Error('Edição de relatórios não disponível offline. Conecte-se à internet.');
    }

    const { data, error } = await supabase
      .from('relatorios')
      .update({
        ...updates,
        updated_at: new Date().toISOString()
      })
      .eq('id', relatorioId)
      .select('*')
      .single();

    if (error) throw error;

    // ✅ ATUALIZAR cache automaticamente
    try {
      localStorage.setItem(`relatorio_${relatorioId}`, JSON.stringify(data));
      localStorage.setItem(`relatorio_${relatorioId}_timestamp`, Date.now().toString());
      
      // Invalidar cache da obra também
      localStorage.removeItem(`relatorios_${data.obra_id}`);
      localStorage.removeItem(`relatorios_${data.obra_id}_timestamp`);
    } catch (cacheError) {
      console.warn('Erro ao atualizar cache de relatório:', cacheError);
    }

    return data;

  } catch (error) {
    console.error('Erro em updateRelatorio:', error);
    throw error;
  }
}

// ✅ NOVA FUNÇÃO: Deletar relatório COM CACHE
export async function deleteRelatorio(relatorioId: string): Promise<void> {
  try {

    if (!isOnline()) {
      throw new Error('Exclusão de relatórios não disponível offline. Conecte-se à internet.');
    }

    // Buscar dados do relatório antes de deletar (para invalidar cache da obra)
    const relatorio = await getRelatorioById(relatorioId);

    const { error } = await supabase
      .from('relatorios')
      .delete()
      .eq('id', relatorioId);

    if (error) throw error;

    // ✅ LIMPAR cache automaticamente
    try {
      localStorage.removeItem(`relatorio_${relatorioId}`);
      localStorage.removeItem(`relatorio_${relatorioId}_timestamp`);
      
      if (relatorio) {
        localStorage.removeItem(`relatorios_${relatorio.obra_id}`);
        localStorage.removeItem(`relatorios_${relatorio.obra_id}_timestamp`);
      }
    } catch (cacheError) {
      console.warn('Erro ao limpar cache de relatório:', cacheError);
    }


  } catch (error) {
    console.error('Erro em deleteRelatorio:', error);
    throw error;
  }
}

// ✅ UTILITÁRIO: Verificar se cache está atualizado
export function isCacheStale(cacheKey: string, maxAgeMinutes: number = 30): boolean {
  try {
    const timestamp = localStorage.getItem(`${cacheKey}_timestamp`);
    if (!timestamp) return true;
    
    const age = Date.now() - parseInt(timestamp);
    return age > (maxAgeMinutes * 60 * 1000);
  } catch (error) {
    return true;
  }
}

// ✅ UTILITÁRIO: Limpar cache de relatórios
export function clearRelatoriosCache(obraId?: string): void {
  try {
    if (obraId) {
      localStorage.removeItem(`relatorios_${obraId}`);
      localStorage.removeItem(`relatorios_${obraId}_timestamp`);
    } else {
      // Limpar todos os caches de relatórios
      Object.keys(localStorage).forEach(key => {
        if (key.startsWith('relatorio')) {
          localStorage.removeItem(key);
        }
      });
    }
    console.log('🧹 Cache de relatórios limpo');
  } catch (error) {
    console.warn('Erro ao limpar cache de relatórios:', error);
  }
}

// ✅ UTILITÁRIO: Formatar tamanho de arquivo
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

export type DiarioCardMetrics = Relatorio & {
  thread_diario_id: string | null;
  comentarios_diario_total: number;
  fotos: number;
  videos: number;
  anexos_outros: number;
  alteracoes_texto_atual: boolean;
  alteracoes_anexos_atual: number;
  comentarios_texto_busca?: string; // texto agregado dos comentários para busca
};

/**
 * Versão via RPC: contadores feitos no banco (sem risco de truncar resultados).
 */
export async function getDiarioCardMetricsByObraRPC(obraId: string, limit = 10000, offset = 0): Promise<DiarioCardMetrics[]> {
  try {
    const { data, error } = await supabase
      .rpc('fn_diario_card_metrics', { p_obra_id: obraId, p_limit: limit, p_offset: offset });
    if (error) throw error;

    const relatorios = (data ?? []).map((r: any) => ({
      id: r.relatorio_id,
      obra_id: r.obra_id,
      numero: r.numero,
      data: r.data,
      status: r.status,
      ultima_modificacao: r.ultima_modificacao,
      clima_manha: r.clima_manha ?? null,
      clima_tarde: r.clima_tarde ?? null,
      clima_noite: r.clima_noite ?? null,
      criado_por: r.criado_por ?? null,
      thread_diario_id: r.thread_diario_id ?? null,
      comentarios_diario_total: r.comentarios_diario_total ?? 0,
      fotos: r.fotos ?? 0,
      videos: r.videos ?? 0,
      anexos_outros: r.anexos_outros ?? 0,
      alteracoes_texto_atual: !!r.alteracoes_texto_atual,
      alteracoes_anexos_atual: r.alteracoes_anexos_atual ?? 0,
      comentarios_texto_busca: '',
    })) as DiarioCardMetrics[];

    const relatorioIds = relatorios.map(r => r.id);
    if (relatorioIds.length === 0) return relatorios;

    const comentariosTextoBusca: Record<string, string> = {};

    // Buscar threads de app_comment_threads (novas)
    const { data: appThreads, error: appThreadsErr } = await supabase
      .from('app_comment_threads')
      .select('id, recurso_id')
      .eq('recurso', 'diario_relatorio')
      .in('recurso_id', relatorioIds);

    console.log('[RPC] app_comment_threads encontradas:', appThreads?.length ?? 0, appThreadsErr ? `Erro: ${appThreadsErr.message}` : '');

    const appThreadIds: string[] = [];
    const appThreadToRelatorio = new Map<string, string>();
    for (const t of appThreads ?? []) {
      appThreadIds.push(t.id);
      appThreadToRelatorio.set(t.id, t.recurso_id as string);
    }

    // Buscar conteúdo de app_comments
    if (appThreadIds.length > 0) {
      const { data: appCommentsContent, error: appCommentsErr } = await supabase
        .from('app_comments')
        .select('thread_id, conteudo')
        .in('thread_id', appThreadIds)
        .is('deleted_at', null)
        .range(0, 999999);

      console.log('[RPC] app_comments encontrados:', appCommentsContent?.length ?? 0, appCommentsErr ? `Erro: ${appCommentsErr.message}` : '');

      for (const c of appCommentsContent ?? []) {
        const relId = appThreadToRelatorio.get(c.thread_id as string);
        if (!relId) continue;
        const existing = comentariosTextoBusca[relId] || '';
        comentariosTextoBusca[relId] = existing ? `${existing} ${c.conteudo}` : c.conteudo;
      }
    }

    // Buscar threads legadas (comment_threads) para relatórios sem thread nova
    const relatoriosComAppThread = new Set(appThreadToRelatorio.values());
    const relatoriosSemAppThread = relatorioIds.filter(id => !relatoriosComAppThread.has(id));

    console.log('[RPC] Relatórios sem app_thread:', relatoriosSemAppThread.length);

    if (relatoriosSemAppThread.length > 0) {
      const { data: legacyThreads, error: legacyThreadsErr } = await supabase
        .from('comment_threads')
        .select('id, recurso_id')
        .eq('recurso', 'diario_relatorio')
        .in('recurso_id', relatoriosSemAppThread);

      console.log('[RPC] comment_threads (legadas) encontradas:', legacyThreads?.length ?? 0, legacyThreadsErr ? `Erro: ${legacyThreadsErr.message}` : '');

      const legacyThreadIds: string[] = [];
      const legacyThreadToRelatorio = new Map<string, string>();
      for (const t of legacyThreads ?? []) {
        legacyThreadIds.push(t.id);
        legacyThreadToRelatorio.set(t.id, t.recurso_id as string);
      }

      // Buscar conteúdo de tarefas_comentarios
      if (legacyThreadIds.length > 0) {
        const { data: legacyCommentsContent, error: legacyCommentsErr } = await supabase
          .from('tarefas_comentarios')
          .select('thread_id, conteudo')
          .in('thread_id', legacyThreadIds)
          .is('deleted_at', null)
          .range(0, 999999);

        console.log('[RPC] tarefas_comentarios (legados) encontrados:', legacyCommentsContent?.length ?? 0, legacyCommentsErr ? `Erro: ${legacyCommentsErr.message}` : '');

        for (const c of legacyCommentsContent ?? []) {
          const relId = legacyThreadToRelatorio.get(c.thread_id as string);
          if (!relId) continue;
          const existing = comentariosTextoBusca[relId] || '';
          comentariosTextoBusca[relId] = existing ? `${existing} ${c.conteudo}` : c.conteudo;
        }
      }
    }

    // Atualizar os relatórios com o texto de busca
    for (const r of relatorios) {
      r.comentarios_texto_busca = comentariosTextoBusca[r.id] || '';
    }

    // Debug: mostrar quantos relatórios têm texto de busca
    const comTexto = relatorios.filter(r => r.comentarios_texto_busca && r.comentarios_texto_busca.length > 0);
    console.log('[RPC] Relatórios com comentarios_texto_busca:', comTexto.length, 'de', relatorios.length);

    return relatorios;
  } catch (err) {
    console.warn('getDiarioCardMetricsByObraRPC falhou, usando consulta direta:', err);
    return getDiarioCardMetricsByObra(obraId);
  }
}

/**
 * Retorna os diários da obra + contadores (comentários, fotos, vídeos, anexos)
 * sem trazer conteúdo de comentários.
 */
export async function getDiarioCardMetricsByObra(obraId: string): Promise<DiarioCardMetrics[]> {
  // 1) Relatórios base
  const { data: relatorios, error: rErr } = await supabase
    .from('relatorios')
    .select('id, obra_id, numero, data, status, ultima_modificacao, clima_manha, clima_tarde, clima_noite, alteracoes_projeto, criado_por')
    .eq('obra_id', obraId)
    .order('data', { ascending: false })
    .order('numero', { ascending: false });
  if (rErr) throw rErr;
  const relatorioIds = (relatorios ?? []).map(r => r.id);
  if (relatorioIds.length === 0) return [];
  const MAX_RANGE = 999999;

  // 2) Threads do diário (recurso = diario_relatorio) - preferir app_comment_threads, com fallback para legado
  const relatorioIdByAppThread = new Map<string, string>();
  const threadRefByRelatorio = new Map<string, { app?: string; legacy?: string }>();

  const { data: appThreads, error: appThreadsError } = await supabase
    .from('app_comment_threads')
    .select('id, recurso_id')
    .eq('recurso', 'diario_relatorio')
    .in('recurso_id', relatorioIds);
  if (appThreadsError) throw appThreadsError;

  for (const t of appThreads ?? []) {
    const relId = t.recurso_id as string;
    const id = t.id as string;
    relatorioIdByAppThread.set(id, relId);
    const ref = threadRefByRelatorio.get(relId) ?? {};
    threadRefByRelatorio.set(relId, { ...ref, app: id });
  }

  const commentsCountByRelatorio: Record<string, number> = {};

  // 3a) Comentários em threads novas (app_comments)
  const appThreadIds = Array.from(relatorioIdByAppThread.keys());
  if (appThreadIds.length > 0) {
    const { data: cRows, error: cErr } = await supabase
      .from('app_comments')
      .select('id, thread_id')
      .in('thread_id', appThreadIds)
      .is('deleted_at', null)
      .range(0, MAX_RANGE);
    if (cErr) throw cErr;
    for (const r of cRows ?? []) {
      const tid = r.thread_id as string;
      const relId = relatorioIdByAppThread.get(tid);
      if (!relId) continue;
      commentsCountByRelatorio[relId] = (commentsCountByRelatorio[relId] ?? 0) + 1;
    }
  }

  // 3b) Threads legadas apenas para relatórios sem thread nova
  const relatoriosSemThreadNova = relatorioIds.filter((id) => !(threadRefByRelatorio.get(id)?.app));
  const legacyThreadIds: string[] = [];
  if (relatoriosSemThreadNova.length > 0) {
    const { data: legacyThreads, error: legacyThreadsError } = await supabase
      .from('comment_threads')
      .select('id, recurso_id')
      .eq('recurso', 'diario_relatorio')
      .in('recurso_id', relatoriosSemThreadNova);

    if (legacyThreadsError) {
      console.warn('Erro ao buscar threads legadas de diários:', legacyThreadsError);
    } else {
      for (const t of legacyThreads ?? []) {
        const relId = t.recurso_id as string;
        const id = t.id as string;
        legacyThreadIds.push(id);
        const ref = threadRefByRelatorio.get(relId) ?? {};
        threadRefByRelatorio.set(relId, { ...ref, legacy: id });
      }
    }
  }

  if (legacyThreadIds.length > 0) {
    const { data: legacyComments, error: legacyCommentsError } = await supabase
      .from('tarefas_comentarios')
      .select('id, thread_id')
      .in('thread_id', legacyThreadIds)
      .is('deleted_at', null)
      .range(0, MAX_RANGE);

    if (legacyCommentsError) {
      console.warn('Erro ao contar comentários legados de diário:', legacyCommentsError);
    } else {
      const relatorioIdByLegacyThread = new Map<string, string>();
      for (const relId of relatoriosSemThreadNova) {
        const legacyThread = threadRefByRelatorio.get(relId)?.legacy;
        if (legacyThread) relatorioIdByLegacyThread.set(legacyThread, relId);
      }

      for (const r of legacyComments ?? []) {
        const tid = r.thread_id as string;
        const relId = relatorioIdByLegacyThread.get(tid);
        if (!relId) continue;
        commentsCountByRelatorio[relId] = (commentsCountByRelatorio[relId] ?? 0) + 1;
      }
    }
  }

  // 4) Mídias por relatório (em lotes para evitar estouro de URL do PostgREST)
  const baseSelect = 'id, relatorio_id';
  const fotosByRel: Record<string, number> = {};
  const videosByRel: Record<string, number> = {};
  const anexosByRel: Record<string, number> = {};
  const BATCH = 80;

  for (let i = 0; i < relatorioIds.length; i += BATCH) {
    const batch = relatorioIds.slice(i, i + BATCH);
    const [fotosQ, videosQ, anexosQ] = await Promise.all([
      supabase.from('fotos').select(baseSelect).in('relatorio_id', batch).range(0, MAX_RANGE),
      supabase.from('videos').select(baseSelect).in('relatorio_id', batch).range(0, MAX_RANGE),
      supabase.from('anexos').select(baseSelect).in('relatorio_id', batch).range(0, MAX_RANGE),
    ]);
    if (fotosQ.error) throw fotosQ.error;
    if (videosQ.error) throw videosQ.error;
    if (anexosQ.error) throw anexosQ.error;

    for (const r of fotosQ.data ?? []) fotosByRel[r.relatorio_id] = (fotosByRel[r.relatorio_id] ?? 0) + 1;
    for (const r of videosQ.data ?? []) videosByRel[r.relatorio_id] = (videosByRel[r.relatorio_id] ?? 0) + 1;
    for (const r of anexosQ.data ?? []) anexosByRel[r.relatorio_id] = (anexosByRel[r.relatorio_id] ?? 0) + 1;
  }

  // 5) Buscar conteúdo dos comentários para pesquisa (app_comments + tarefas_comentarios)
  const comentariosTextoBusca: Record<string, string> = {};

  console.log('[DIRECT] app_comment_threads encontradas:', appThreadIds.length);

  // 5a) Comentários novos (app_comments)
  if (appThreadIds.length > 0) {
    const { data: commentsContent, error: ccErr } = await supabase
      .from('app_comments')
      .select('thread_id, conteudo')
      .in('thread_id', appThreadIds)
      .is('deleted_at', null)
      .range(0, MAX_RANGE);

    console.log('[DIRECT] app_comments encontrados:', commentsContent?.length ?? 0, ccErr ? `Erro: ${ccErr.message}` : '');

    if (!ccErr && commentsContent) {
      for (const c of commentsContent) {
        const tid = c.thread_id as string;
        const relId = relatorioIdByAppThread.get(tid);
        if (!relId) continue;
        const existing = comentariosTextoBusca[relId] || '';
        comentariosTextoBusca[relId] = existing ? `${existing} ${c.conteudo}` : c.conteudo;
      }
    }
  }

  console.log('[DIRECT] Relatórios sem app_thread (legados):', legacyThreadIds.length);

  // 5b) Comentários legados (tarefas_comentarios)
  if (legacyThreadIds.length > 0) {
    const relatorioIdByLegacyThread = new Map<string, string>();
    for (const relId of relatoriosSemThreadNova) {
      const legacyThread = threadRefByRelatorio.get(relId)?.legacy;
      if (legacyThread) relatorioIdByLegacyThread.set(legacyThread, relId);
    }

    const { data: legacyCommentsContent, error: lccErr } = await supabase
      .from('tarefas_comentarios')
      .select('thread_id, conteudo')
      .in('thread_id', legacyThreadIds)
      .is('deleted_at', null)
      .range(0, MAX_RANGE);

    console.log('[DIRECT] tarefas_comentarios encontrados:', legacyCommentsContent?.length ?? 0, lccErr ? `Erro: ${lccErr.message}` : '');

    if (!lccErr && legacyCommentsContent) {
      for (const c of legacyCommentsContent) {
        const tid = c.thread_id as string;
        const relId = relatorioIdByLegacyThread.get(tid);
        if (!relId) continue;
        const existing = comentariosTextoBusca[relId] || '';
        comentariosTextoBusca[relId] = existing ? `${existing} ${c.conteudo}` : c.conteudo;
      }
    }
  }

  // 6) Montagem final
  const resultado = (relatorios ?? []).map((r) => {
    const threadRef = threadRefByRelatorio.get(r.id);
    const threadId = threadRef?.app ?? threadRef?.legacy ?? null;
    const comentarios = commentsCountByRelatorio[r.id] ?? 0;
    const altTexto = !!(r.alteracoes_projeto?.texto && String(r.alteracoes_projeto?.texto).trim().length > 0);
    const altAnexos = Array.isArray(r.alteracoes_projeto?.anexos) ? r.alteracoes_projeto.anexos.length : 0;
    return {
      ...r,
      thread_diario_id: threadId,
      comentarios_diario_total: comentarios,
      fotos: fotosByRel[r.id] ?? 0,
      videos: videosByRel[r.id] ?? 0,
      anexos_outros: anexosByRel[r.id] ?? 0,
      alteracoes_texto_atual: altTexto,
      alteracoes_anexos_atual: altAnexos,
      comentarios_texto_busca: comentariosTextoBusca[r.id] || '',
    } as DiarioCardMetrics;
  });

  // Debug: mostrar quantos relatórios têm texto de busca
  const comTexto = resultado.filter(r => r.comentarios_texto_busca && r.comentarios_texto_busca.length > 0);
  console.log('[DIRECT] Relatórios com comentarios_texto_busca:', comTexto.length, 'de', resultado.length);

  return resultado;
}
